"use client";

import Link from "next/link";
import { AlertCircle, CheckCircle2, Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

export type BatchFileRow = {
  id: number;
  filename: string;
  status: string;
  invoice_id: number | null;
  error_message: string | null;
};

const STATUS_LABELS: Record<string, string> = {
  pending: "Pending",
  uploading: "Uploading",
  parsing: "Parsing",
  matching: "Matching",
  review: "Needs review",
  done: "Done",
  error: "Error",
};

function StatusIcon({ status }: { status: string }) {
  if (status === "done") return <CheckCircle2 className="size-4 text-green-600" />;
  if (status === "error") return <AlertCircle className="size-4 text-destructive" />;
  if (status === "review") return <AlertCircle className="size-4 text-amber-500" />;
  return <Loader2 className="size-4 animate-spin text-muted-foreground" />;
}

type Props = {
  batchId: number;
  files: BatchFileRow[];
  completed: number;
  total: number;
};

export function BatchProgress({ files, completed, total }: Props) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">
          Processing {completed}/{total} files
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {files.map((f) => (
          <div key={f.id} className="flex items-center gap-3 rounded-md border p-3">
            <StatusIcon status={f.status} />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium">{f.filename}</p>
              {f.error_message && (
                <p className="text-xs text-destructive">{f.error_message}</p>
              )}
            </div>
            <Badge variant="outline">{STATUS_LABELS[f.status] ?? f.status}</Badge>
            {f.status === "review" && f.invoice_id && (
              <Button asChild size="sm" variant="secondary">
                <Link href={`/review/${f.invoice_id}`}>Review</Link>
              </Button>
            )}
            {f.status === "done" && f.invoice_id && (
              <Button asChild size="sm" variant="ghost">
                <Link href={`/dashboard?invoice=${f.invoice_id}`}>View</Link>
              </Button>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
